import { Link } from "react-router-dom";
import { Clock, ArrowRight } from "lucide-react";
import { SERVICE_CATEGORIES } from "../data/business";
import { SectionHeading } from "../components/SectionHeading";
import { Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { CTASection } from "../components/CTASection";
import { useSEO } from "../hooks/useSEO";

export default function Services() {
  useSEO(
    "Services | New Lookz Hair Salon — Gandhi Nagar, Hyderabad",
    "Haircuts, hair styling, hair care and grooming at New Lookz Hair Salon, Gandhi Nagar, Hyderabad. Prices on request — call to confirm.",
    "/services"
  );

  return (
    <>
      <section className="bg-ink grain" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <SectionHeading
            dark
            eyebrow="Services"
            title="A Menu Built Around Your Look"
            text="Draft service menu — names, details and prices are confirmed directly by the salon. Where a price is not yet confirmed, it shows as “Price on request”."
          />
          <Reveal delay={0.15} className="mt-8">
            <Link to="/book" data-testid="services-top-book" className="inline-flex items-center gap-2 rounded-full bg-copper px-8 py-4 font-display font-bold text-ink transition-all duration-300 hover:bg-copper-hover hover:-translate-y-0.5">
              Book an Appointment <ArrowRight size={16} aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>

      <Marquee dark={false} />

      <section className="bg-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 space-y-20">
          {SERVICE_CATEGORIES.map((cat, ci) => (
            <div key={cat.slug} id={cat.slug} className="scroll-mt-28" data-testid={`service-category-${cat.slug}`}>
              <Reveal>
                <div className="grid lg:grid-cols-5 gap-8 items-end mb-8">
                  <div className="lg:col-span-3">
                    <p className="font-editorial italic text-copper text-xl">{String(ci + 1).padStart(2, "0")}</p>
                    <h2 className="font-display font-extrabold text-ink text-[clamp(1.8rem,3vw,2.6rem)] mt-1">{cat.title}</h2>
                    <p className="mt-3 text-mute text-base leading-relaxed max-w-xl">{cat.blurb}</p>
                  </div>
                  <div className="lg:col-span-2 rounded-xl overflow-hidden border border-ink/10">
                    <img src={cat.image} alt={cat.imageAlt} loading="lazy" width={900} height={563} className="w-full aspect-[16/9] object-cover" />
                  </div>
                </div>
              </Reveal>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {cat.services.map((s, i) => (
                  <Reveal key={s.name} delay={i * 0.07} className="h-full">
                    <article className="h-full rounded-xl bg-paper border border-ink/10 p-6 flex flex-col transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_45px_-20px_rgba(17,17,17,0.3)]" data-testid={`service-item-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <h3 className="font-display font-bold text-lg text-ink">{s.name}</h3>
                      <p className="mt-2 text-sm text-mute leading-relaxed flex-1">{s.description}</p>
                      <div className="mt-5 flex items-center justify-between gap-3 text-sm">
                        <span className="font-display font-bold text-copper" data-testid="service-price">{s.price}</span>
                        <span className="inline-flex items-center gap-1.5 text-mute text-xs"><Clock size={13} aria-hidden="true" /> {s.duration}</span>
                      </div>
                      <Link
                        to="/book"
                        data-testid={`service-book-${cat.slug}-${i}`}
                        className="mt-5 rounded-full border border-ink/15 text-center font-display font-semibold text-sm text-ink py-2.5 transition-colors hover:border-copper hover:text-copper"
                      >
                        Book This Service
                      </Link>
                    </article>
                  </Reveal>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <CTASection />
    </>
  );
}
